import budgetService from "api/budgetService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import {
  ENGAGEMENT_METRIC_ELEMENT_ID,
  IBudgetEngagementDocument,
  engagementElementLabel,
} from "dto/Budget/IBudgetEngagementDocument";
import { useEngagementSigning } from "./useEngagementSigning";
import styles from "./styles.module.scss";

const fmtDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pl-PL", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "";

/** Przycisk „Pobierz XML” — surowy dokument (np. do weryfikacji podpisu poza Notą). */
export const EngagementDocumentDownload = ({
  doc,
}: {
  doc: IBudgetEngagementDocument;
}) => (
  <HollowButton
    text="Pobierz XML"
    onClick={() => {
      const blob = new Blob([doc.Xml], { type: "text/xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.FileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }}
  />
);

interface Props {
  engagementId: string;
  /** Podgląd HTML dokumentu (iframe). Bez niego — tylko podpisy i przyciski. */
  showPreview?: boolean;
  /** Wysokość podglądu (px lub CSS). */
  previewHeight?: number | string;
  onSigned?: (doc: IBudgetEngagementDocument) => void;
}

/**
 * Dokument wniosku o zaangażowanie środków budżetowych (XML WnioZaangBudz z BE,
 * port z Magistratu): podgląd, lista podpisów elektronicznych i podpis elementów
 * czekających na bieżącego użytkownika (metryka wnioskodawcy / własny etap akceptacji).
 */
const EngagementDocumentPanel = ({
  engagementId,
  showPreview = true,
  previewHeight = 520,
  onSigned,
}: Props) => {
  const { data: doc, isLoading } =
    budgetService.useGetEngagementDocument(engagementId);
  const { requestSign, signing, pluginUnavailable, modal } =
    useEngagementSigning(engagementId, onSigned);

  if (isLoading) return <div className={styles.hint}>Ładowanie dokumentu…</div>;
  if (!doc) {
    return (
      <div className={styles.hint}>
        Brak dokumentu wniosku — zaangażowanie nie przeszło przez procedurę
        akceptacji.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {showPreview && (
        <iframe
          className={styles.preview}
          style={{ height: previewHeight }}
          title="Wniosek o zaangażowanie środków budżetowych"
          sandbox=""
          srcDoc={doc.Html}
        />
      )}

      <div className={styles.signatures}>
        <div className={styles.label}>Podpisy elektroniczne</div>
        {doc.Signatures.length === 0 ? (
          <div className={styles.hint}>
            Brak podpisów elektronicznych — dokument ma podpisy tekstowe.
          </div>
        ) : (
          <ul className={styles.list}>
            {doc.Signatures.map((s) => (
              <li key={s.ElementId}>
                <span className={styles.who}>
                  {s.SignerName || "podpis kwalifikowany"}
                </span>
                <span className={styles.what}>
                  {engagementElementLabel(s.ElementId)}
                  {s.IssuerName ? ` · ${s.IssuerName}` : ""}
                </span>
                <span className={styles.when}>{fmtDateTime(s.SigningTime)}</span>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.actions}>
          {doc.MySignableElementIds.map((id) => (
            <Button
              key={id}
              text={
                id === ENGAGEMENT_METRIC_ELEMENT_ID
                  ? "Podpisz wniosek elektronicznie"
                  : "Podpisz swój etap elektronicznie"
              }
              loading={signing}
              disabled={pluginUnavailable}
              onClick={() => requestSign(id)}
            />
          ))}
          <EngagementDocumentDownload doc={doc} />
          {doc.MySignableElementIds.length > 0 && pluginUnavailable && (
            <span className={styles.hint}>
              Uruchom wtyczkę Nota, aby podpisać certyfikatem.
            </span>
          )}
        </div>
      </div>

      {modal}
    </div>
  );
};

export default EngagementDocumentPanel;
